import React, { useState, useEffect } from "react";
import PrimaryButton from "../components/PrimaryButton";
import BoardOfOfficials from "../components/BoardOfOfficials";
import { Link } from "react-router-dom";

const About = () => {
  const [isBlurred, setIsBlurred] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Get the board section position
      const boardSection = document.getElementById("board");
      if (boardSection) {
        const rect = boardSection.getBoundingClientRect();
        // When the board section starts coming into view (100px before it appears)
        if (rect.top <= window.innerHeight + 100) {
          setIsBlurred(true);
          // Add a class to the body for potential global style changes
          document.body.classList.add("board-section-visible");
        } else {
          setIsBlurred(false);
          document.body.classList.remove("board-section-visible");
        }
      }
    };

    // Run once on mount to check initial position
    handleScroll();

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.body.classList.remove("board-section-visible");
    };
  }, []);

  return (
    <div className="relative">
      {/* Fixed Parallax Background Image */}
      <div className="fixed inset-0 z-0 transition-all duration-1000">
        <div
          className={`absolute inset-0 bg-blue-900/30 transition-opacity duration-1000 ${
            isBlurred ? "opacity-60" : "opacity-0"
          }`}
        ></div>
        <img
          src="/about/sililara.jpg"
          alt="UMMES Graduation Day"
          className={`w-full h-full object-cover transition-all duration-1000 ${
            isBlurred ? "blur-lg scale-105" : ""
          }`}
        />
      </div>

      {/* Content that scrolls over the background */}
      <div className="relative z-10">
        {/* Hero Section with About Us at top */}
        <section className="relative h-[70vh] flex items-start justify-center pt-32">
          {/* Intensified gradient overlay for text readability - slightly increased height */}
          <div className="absolute inset-0 bg-gradient-to-b from-white via-white/90 to-transparent h-2/5"></div>

          {/* Content */}
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-6xl md:text-5xl font-extrabold mb-4 text-black tracking-tight drop-shadow-lg animate-fadein font-manrope">
              About Us
            </h1>
          </div>
        </section>

        {/* About UMMES Paragraph Section */}
        <section className="py-16 bg-white/95 dark:bg-neutral-800/95 backdrop-blur-sm transition-colors duration-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <p className="text-lg text-gray-700 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed text-justify">
                The University of Moratuwa Mechanical Engineering Society
                (UMMES) serves as a key platform for collaboration, learning,
                and professional engagement within the Department of Mechanical
                Engineering. It acts as a hub where students, staff, alumni, and
                industry partners can connect, exchange knowledge, and work
                together to strengthen both academic and professional ties.
                <br />
                <br />
                Established to enhance university–industry relationships and to
                support departmental initiatives, UMMES plays an active role in
                creating vibrant experiences that go beyond classroom learning.
                By organizing discussions, events, and collaborative programs,
                the society helps students gain exposure to real-world
                engineering challenges while also enabling alumni and
                professionals to stay engaged with the department.
                <br />
                <br />
                The society is guided by an Executive Committee consisting of
                undergraduate students and academic staff. This joint structure
                ensures that activities are student-driven yet aligned with
                departmental goals, allowing UMMES to maintain its role as a
                bridge between academia, industry, and the wider mechanical
                engineering community.
              </p>
            </div>
          </div>
        </section>

        {/* Board of Officials Section */}
        <BoardOfOfficials />

        {/* Events & Initiatives */}
        <section className="py-16 bg-gray-800/95 dark:bg-gray-900/95 backdrop-blur-sm transition-colors duration-200 relative">
          {/* Gradient overlay from bottom to top */}
          <div className="absolute inset-0 bg-gradient-to-t from-gray-800 via-gray-800/80 to-transparent"></div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="animate-slide-up">
                <img
                  src="/about/events.png"
                  alt="UMMES Events and Initiatives"
                  className="w-full max-w-xl mx-auto rounded-lg shadow-lg"
                />
              </div>
              <div className="animate-slide-up">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-manrope">
                  Events & Initiatives
                </h2>
                <div className="prose prose-lg text-gray-300 space-y-4 text-justify">
                  <p>
                    UMMES organizes a diverse range of events and initiatives
                    throughout the academic year, designed to enhance the
                    learning experience and professional development of our
                    members. From technical workshops and industry seminars to
                    networking events and competitions, we provide opportunities
                    for students to engage with cutting-edge technologies and
                    connect with industry professionals.
                  </p>
                  <p>
                    Our initiatives focus on bridging the gap between
                    theoretical knowledge and practical application, ensuring
                    that our members are well-prepared for their future careers
                    in mechanical engineering. We believe in creating an
                    environment that fosters innovation, collaboration, and
                    continuous learning.
                  </p>
                  <div className="mt-8 text-center lg:text-left">
                    <Link to="/events" className="inline-block">
                      <PrimaryButton>
                        <span className="flex items-center">
                          View All Events
                          <svg
                            className="ml-2 w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                      </PrimaryButton>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Student Life */}
        <section className="py-16 bg-gray-700 dark:bg-gray-800 transition-colors duration-200 relative overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0 z-0">
            <img
              src="/about/about_hero.jpg"
              alt="UMMES Events Background"
              className="w-full h-full object-cover opacity-20"
            />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="animate-slide-up order-1 lg:order-2 mb-6">
                <img
                  src="/about/mech.jpg"
                  alt="UMMES Student Life"
                  className="w-full max-w-xl mx-auto rounded-lg shadow-lg"
                />
              </div>
              <div className="animate-slide-up order-2 lg:order-1">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-manrope">
                  Student Life
                </h2>
                <div className="prose prose-lg text-gray-300 space-y-4 text-justify">
                  <p>
                    Life as a mechanical engineering student at the University
                    of Moratuwa is enriched through UMMES's vibrant community
                    activities and support systems. We create an environment
                    where academic excellence meets personal growth, fostering
                    friendships and professional relationships that last a
                    lifetime.
                  </p>
                  <p>
                    From study groups and peer mentoring programs to
                    recreational activities and cultural events, UMMES ensures
                    that every student feels connected and supported throughout
                    their academic journey. We believe that a well-rounded
                    student experience is essential for developing not just
                    technical skills, but also leadership qualities and
                    interpersonal abilities that are crucial for future success.
                  </p>
                  <div className="mt-8 text-center lg:text-left">
                    <Link to="/events" className="inline-block">
                      <PrimaryButton>
                        <span className="flex items-center">
                          Student Life
                          <svg
                            className="ml-2 w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                      </PrimaryButton>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;
